import { List, MagnifyingGlass } from "@phosphor-icons/react";
import { Link, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TldrButton } from "~/features/command/TldrButton";
import { ModelChooser } from "~/features/talk/ModelChooser";
import { WorkspaceThinkerComposer } from "~/features/thinkers/workspace/WorkspaceThinkerComposer";
import { IconButton } from "~/foundation/components/IconButton";
import { Tooltip } from "~/foundation/components/Tooltip";
import { depths } from "~/foundation/styles/depths";
import { PinnedBar } from "~/shell/ConsoleBar/PinnedBar";
import { ProductPicker } from "~/shell/ConsoleBar/ProductPicker";
import { StatusCluster } from "~/shell/ConsoleBar/StatusCluster";
import { WorkspaceMenu } from "~/shell/ConsoleBar/WorkspaceMenu";
import { toggleNavigation, useShellStore } from "~/shell/store";
import { SITE_CONFIG } from "~/site";

export interface ConsoleBarProps {
  consoleStatusItems?: ReactNode[];
}

/**
 * 48px, full width, above everything (the GCP/AWS console idiom): identity and global
 * navigation stay put while the sidebar below changes with context.
 */
export function ConsoleBar({ consoleStatusItems }: ConsoleBarProps) {
  const { t } = useTranslation();
  const setCommandBarOpen = useShellStore((state) => state.setCommandBarOpen);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });

  return (
    <header
      className="col-span-2 flex h-[var(--console-bar-height)] min-w-0 flex-wrap items-center gap-x-1 gap-y-0 border-b border-divider bg-console-bar-bg px-2 pb-1 lg:flex-nowrap lg:pb-0"
      style={{ zIndex: depths.consoleBar }}
    >
      <Tooltip content={t("Toggle sidebar")} shortcut={["Meta", "."]}>
        <IconButton icon={List} aria-label={t("Toggle sidebar")} onClick={toggleNavigation} />
      </Tooltip>

      <Link
        to="/"
        className="mx-1 shrink-0 text-body font-medium text-text hover:opacity-80"
        aria-label={t("Go to home")}
      >
        {SITE_CONFIG.wordmark}
      </Link>

      <div className="min-w-0 max-w-24 [&_button]:max-w-full sm:max-w-none">
        <WorkspaceMenu />
      </div>
      <div className="hidden md:block">
        <ProductPicker />
      </div>
      <div className="hidden xl:block">
        <PinnedBar />
      </div>

      <div className="flex-1 lg:hidden" />
      <WorkspaceThinkerComposer />

      <button
        type="button"
        onClick={() => setCommandBarOpen(true)}
        aria-label={t("Search…")}
        className="flex h-8 items-center gap-2 rounded-sm border border-input-border bg-bg px-2 text-body text-text-tertiary hover:border-input-border-focus"
      >
        <MagnifyingGlass size={16} aria-hidden className="shrink-0" />
      </button>

      {/* W7 — the other half of the palette, beside the search affordance rather than as a
          floating FAB: kms removed its ✨ FAB precisely because it collided with this shell. */}
      {/* Who answers, with which model — and, while something runs, the model at work. */}
      <div className="hidden 2xl:block">
        <ModelChooser />
      </div>

      {/* "put some sort of tldr button in all screens" (owner, 2026-10-21) — the TL;DR of whatever this is */}
      <TldrButton pathname={pathname} search={searchStr} />

      <StatusCluster {...(consoleStatusItems ? { consoleStatusItems } : {})} />
    </header>
  );
}
